import { FC, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import lodash from 'lodash';
import { useSnackbar } from 'notistack';
import { Button, Tooltip } from '@mui/material';
import IosShareIcon from '@mui/icons-material/IosShare';
import ScrollItem from 'src/components/Items/ScrollItem';
import ShareModal from 'src/components/Items/ShareModal';
import VanHost from './Host';
import VanPhotos from './Photos';
import VanExtras from './Extras';
import SingleVanModal from '../Modal/SingleVanModal';
import { useTypedSelector } from 'src/hooks/useTypeSelector';
import useIsMobile from 'src/hooks/useIsMobile';
import { useActions } from 'src/hooks/useActions';
import { VanFilterErrors } from 'src/store/reducers/van/types';
import { cutText, scrollToTop } from 'src/common/utils/others';
import showFilterErrors from 'src/common/utils/showFilterErrors';
import { dineroFormat } from 'src/common/utils/dataFormat';
import { IVanFetch } from 'src/models/IVan';
import './styles.scss';
import './media.scss';

interface IProps {
  van: IVanFetch;
  region?: string;
  isFilterValidate: boolean;
  errors: VanFilterErrors;
  container?: Element;
}

const VanItem: FC<IProps> = ({ van, region, isFilterValidate, errors, container }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { setupOrder, setCheckFilter, setLocationFilter } = useActions();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const {
    location: { locationList }
  } = useTypedSelector(state => state);
  const selectedLocation = useTypedSelector(state => state.van.filters.location);
  const { id, description, photos, mark, price, currency, near_van, tags, host, features } = van;

  // nearVan logic
  // const [deliveryPrice, setDeliveryPrice] = useState('');
  // useEffect(() => {
  //   selectedLocation &&
  //     near_van.map((nearVan) => (
  //       nearVan.location === selectedLocation
  //       && setDeliveryPrice(dineroFormat(nearVan.price, nearVan.currency))
  //     ));
  // }, [selectedLocation]);

  // subLocation logic
  const [subLocationNames, setSubLocationNames] = useState<string[]>([]);
  useEffect(() => {
    const locationTags = tags.filter(tag => tag.category === 'sublocation').map(i => i.name);
    const subNames = locationList
      .find(location => location.name === selectedLocation)
      ?.sub_location.map(sub => sub.name);
    if (subNames) {
      const intersection = lodash.intersection(locationTags, subNames);
      setSubLocationNames(intersection);
    }
  }, [selectedLocation]);

  const [openVanModal, setOpenVanModal] = useState(false);
  const [isOpenShareInModal, setIsOpenShareInModal] = useState(false);
  const [openShare, setOpenShare] = useState(false);
  const handleOpenVanModal = () => setOpenVanModal(true);
  const handleOpenShare = () => setOpenShare(true);
  const handleOpenVanModalWithShare = () => {
    setOpenVanModal(true);
    setIsOpenShareInModal(true);
  };
  const handleCloseVanModal = () => {
    setOpenVanModal(false);
    setIsOpenShareInModal(false);
  };

  const handleOrder = () => {
    if (isFilterValidate) {
      // if (region) {
      //   selectedLocation !== region
      //     && setLocationFilter(region, '');
      // }
      setupOrder(van);
      navigate('/order');
    } else {
      setCheckFilter();
      !isMobile && showFilterErrors(enqueueSnackbar, errors);
      if (container) {
        container!.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else scrollToTop();
    }
  };

  return (
    <div className='van__card'>
      <VanPhotos photos={photos} />
      <div>
        <div className='van__info'>
          {isMobile ? (
            <Link to={`/van/143${id}`} target='_blank' className='van__info__name'>
              {mark}
            </Link>
          ) : (
            <div className='van__info__name' onClick={handleOpenVanModal}>
              {mark}
            </div>
          )}
          <Tooltip title='Share van link'>
            <IosShareIcon onClick={isMobile ? handleOpenShare : handleOpenVanModalWithShare} />
          </Tooltip>
          <div className='van__info__price'>
            {dineroFormat(price, currency)}
            <div className='van__info__price__description'>average per day</div>
          </div>
        </div>
        {subLocationNames.length ? (
          <ScrollItem array={subLocationNames} id={id} className='sub-locations' />
        ) : (
          <></>
        )}
        <div className='van__info__description'>
          {cutText(description, 220)}
          {isMobile ? (
            <Link to={`/van/143${id}`} target='_blank' className='van__info__description_more'>
              read more
            </Link>
          ) : (
            <div className='van__info__description_more' onClick={handleOpenVanModal}>
              read more
            </div>
          )}
        </div>
        <div className='van__capacity'>
          <div className='van__capacity__title'>Features:</div>
          <ScrollItem id={id} van={van} array={features} className='van__capacity__container' />
        </div>
        <div className='van__order'>
          {/* {selectedLocation && (selectedLocation !== location) &&
            <div className='van__info__near-van mobile-show'>
              {deliveryPrice ? (
                <>
                  Delivery from {location} will cost <b>{deliveryPrice}</b>
                </>
              ) : (
                <span className='van__info__near-van__no-price'>
                  <Icons.Warning />
                  <span>
                    Please contact the owner to clarify the price for delivery.
                  </span>
                </span>
              )}
            </div>
          } */}
          <div
            className={`
            van__order__container ${van.type === 'passenger' && 'van__order__delivery-container'}
          `}
          >
            <VanHost host={host} />
            <VanExtras type={van.type} />
          </div>
          <div className='van__order__block'>
            <Button
              className='van__order__btn'
              onClick={handleOrder}
              children='Book / Quote'
              variant='contained'
            />
          </div>
        </div>
      </div>
      {/* {selectedLocation && (selectedLocation !== location) &&
        <div className='van__info__near-van mobile-hide'>
          {deliveryPrice ? (
            <>
              Delivery from {location} will cost <b>{deliveryPrice}</b>
            </>
          ) : (
            <span className='van__info__near-van__no-price'>
              <Icons.Warning />
              <span>
                Please contact the owner to clarify the price for delivery.
              </span>
            </span>
          )}
        </div>
      } */}
      <SingleVanModal
        open={openVanModal}
        handleClose={handleCloseVanModal}
        van={van}
        isOpenShare={isOpenShareInModal}
      />
      <ShareModal
        url={`https://van.sprinter-rentals.com/van/143${van.id}`}
        open={openShare}
        onClose={() => setOpenShare(false)}
        className='down'
      />
    </div>
  );
};
export default VanItem;
